import React, { createContext, useContext, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useEmailStore } from '../stores/root_store';
import type { Email, EmailHistory, SendEmailParams } from '../api/email_api';
import type { FileRecord } from '../api/file_api';

export interface EmailContextType {
  // State
  loading: boolean;
  sending: boolean;
  error: string | null;

  // Actions
  sendEmail: (
    associatedType: string,
    associatedUuid: string,
    toEmail: string,
    fromEmail: string,
    subject: string,
    content: string,
    files?: FileRecord[],
    replyToEmailUuid?: string
  ) => Promise<Email | undefined>;
  resendEmail: (emailUuid: string) => Promise<Email | undefined>;

  // Data access
  getEmailsByAssociation: (
    associatedType: string,
    associatedUuid: string
  ) => Email[];
  fetchEmailsByAssociation: (
    associatedType: string,
    associatedUuid: string
  ) => Promise<void>;
  getEmailHistory: (emailUuid: string) => EmailHistory[];
  fetchEmailHistory: (emailUuid: string) => Promise<void>;
}

const EmailContext = createContext<EmailContextType | null>(null);

export const EmailProvider = observer(function EmailProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const emailStore = useEmailStore();

  // Send email
  const sendEmail = useCallback(
    async (
      associatedType: string,
      associatedUuid: string,
      toEmail: string,
      fromEmail: string,
      subject: string,
      content: string,
      files?: FileRecord[],
      replyToEmailUuid?: string
    ): Promise<Email | undefined> => {
      const params: SendEmailParams = {
        associated_type: associatedType,
        associated_uuid: associatedUuid,
        to_email: toEmail,
        from_email: fromEmail,
        subject,
        content,
        files: files?.map((f) => f.uuid).join(','),
        reply_to_email_uuid: replyToEmailUuid,
      };

      return emailStore.sendEmail(params);
    },
    [emailStore]
  );

  // Resend email
  const resendEmail = useCallback(
    async (emailUuid: string): Promise<Email | undefined> => {
      return emailStore.resendEmail(emailUuid);
    },
    [emailStore]
  );

  // Get emails by association
  const getEmailsByAssociation = useCallback(
    (associatedType: string, associatedUuid: string): Email[] => {
      return emailStore.getEmailsByAssociation(associatedType, associatedUuid);
    },
    [emailStore]
  );

  // Fetch emails by association
  const fetchEmailsByAssociation = useCallback(
    async (associatedType: string, associatedUuid: string): Promise<void> => {
      await emailStore.fetchByAssociation(
        associatedType,
        associatedUuid
      );
    },
    [emailStore]
  );

  // Get email history
  const getEmailHistory = useCallback(
    (emailUuid: string): EmailHistory[] => {
      return emailStore.getHistory(emailUuid);
    },
    [emailStore]
  );

  // Fetch email history
  const fetchEmailHistory = useCallback(
    async (emailUuid: string): Promise<void> => {
      await emailStore.fetchHistory(emailUuid);
    },
    [emailStore]
  );

  const value: EmailContextType = {
    loading: emailStore.loading,
    sending: emailStore.sending,
    error: emailStore.error,
    sendEmail,
    resendEmail,
    getEmailsByAssociation,
    fetchEmailsByAssociation,
    getEmailHistory,
    fetchEmailHistory,
  };

  return (
    <EmailContext.Provider value={value}>{children}</EmailContext.Provider>
  );
});

// eslint-disable-next-line react-refresh/only-export-components
export function useEmail(): EmailContextType {
  const context = useContext(EmailContext);
  if (!context) {
    throw new Error('useEmail must be used within an EmailProvider');
  }
  return context;
}
